import { useState, useMemo } from 'react';
import { BusinessPlanTemplate, businessPlanTemplates } from '../../../mocks/businessPlanTemplates';

interface Props {
  template: BusinessPlanTemplate;
  onClose: () => void;
  onUse: () => void;
  onSwitchTemplate?: (t: BusinessPlanTemplate) => void;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
}

const BADGE_STYLES: Record<string, string> = {
  Popular: 'bg-amber-100 text-amber-700 border border-amber-200',
  New: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  Trending: 'bg-rose-100 text-rose-700 border border-rose-200',
};

// ─── Recommendation Engine ────────────────────────────────────────────────────
function scoreTemplates(
  source: BusinessPlanTemplate,
  pool: BusinessPlanTemplate[]
): Array<{ template: BusinessPlanTemplate; score: number; reasons: string[] }> {
  return pool
    .filter(t => t.id !== source.id)
    .map(t => {
      let score = 0;
      const reasons: string[] = [];

      // Same category → highest weight
      if (t.category === source.category) {
        score += 40;
        reasons.push('Same industry');
      }

      // Shared tags
      const sharedTags = t.tags.filter(tag => source.tags.includes(tag));
      if (sharedTags.length > 0) {
        score += sharedTags.length * 12;
        reasons.push(`${sharedTags.length} shared style${sharedTags.length > 1 ? 's' : ''}`);
      }

      // Shared sections
      const sharedSections = t.sections.filter(s => source.sections.includes(s));
      if (sharedSections.length > 0) {
        score += sharedSections.length * 5;
        reasons.push(`${sharedSections.length} common section${sharedSections.length > 1 ? 's' : ''}`);
      }

      // Similar page count (within ±8 pages)
      const pageDiff = Math.abs(t.pageCount - source.pageCount);
      if (pageDiff <= 4) {
        score += 15;
        reasons.push('Similar length');
      } else if (pageDiff <= 8) {
        score += 7;
        reasons.push('Close length');
      }

      // Same badge
      if (t.badge && t.badge === source.badge) {
        score += 5;
      }

      return { template: t, score, reasons };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);
}

// Normalise score to a 0-100 match percentage
function toMatchPct(score: number): number {
  return Math.min(100, Math.round((score / 90) * 100));
}

function MatchBadge({ pct }: { pct: number }) {
  const color =
    pct >= 80 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
    pct >= 55 ? 'bg-amber-100 text-amber-700 border-amber-200' :
                'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${color} whitespace-nowrap`}>
      {pct}% match
    </span>
  );
}

// ─── Similar Templates Panel ──────────────────────────────────────────────────
function SimilarTemplatesPanel({
  source,
  onSwitch,
  onUse,
}: {
  source: BusinessPlanTemplate;
  onSwitch: (t: BusinessPlanTemplate) => void;
  onUse: () => void;
}) {
  const recommendations = useMemo(() => scoreTemplates(source, businessPlanTemplates), [source]);

  if (recommendations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400">
        <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100 mb-3">
          <i className="ri-search-line text-2xl"></i>
        </div>
        <p className="text-sm font-medium">No similar templates found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Engine description */}
      <div className="flex items-start gap-3 bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100 rounded-2xl p-4">
        <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-violet-100 flex-shrink-0">
          <i className="ri-sparkling-2-line text-violet-600 text-base"></i>
        </div>
        <div>
          <p className="text-xs font-bold text-violet-700 mb-0.5">AI Recommendation Engine</p>
          <p className="text-xs text-slate-500 leading-relaxed">
            Scored by shared industry, style tags, common sections, and similar length — ranked by best overall fit.
          </p>
        </div>
      </div>

      {/* Cards */}
      {recommendations.map(({ template: t, score, reasons }) => {
        const [primary, accent] = t.colorPalette;
        const pct = toMatchPct(score);
        return (
          <div
            key={t.id}
            className="group flex items-start gap-4 p-4 rounded-2xl border border-slate-100 hover:border-violet-200 hover:bg-violet-50/20 transition-all"
          >
            {/* Color swatch */}
            <div
              className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${primary}22, ${accent}33)` }}
            >
              <i className="ri-file-text-line text-xl" style={{ color: primary }}></i>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <MatchBadge pct={pct} />
                {t.badge && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${BADGE_STYLES[t.badge]}`}>
                    {t.badge}
                  </span>
                )}
              </div>
              <p className="text-sm font-bold text-slate-800 group-hover:text-violet-700 transition-colors leading-tight mb-1 truncate">
                {t.name}
              </p>
              <p className="text-xs text-slate-400 mb-2 line-clamp-1">{t.description}</p>

              {/* Reason chips */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {reasons.map(r => (
                  <span key={r} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">
                    <i className="ri-check-line mr-0.5 text-emerald-500"></i>{r}
                  </span>
                ))}
              </div>

              {/* Meta + actions */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-pages-line"></i>{t.pageCount} pages
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-list-check-2"></i>{t.sections.length} sections
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-price-tag-3-line"></i>{t.category}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => onSwitch(t)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50 transition-all cursor-pointer whitespace-nowrap font-medium"
                  >
                    Preview
                  </button>
                  <button
                    onClick={onUse}
                    className="text-xs px-3 py-1.5 rounded-lg text-white font-semibold cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}
                  >
                    Use
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Sample Content ───────────────────────────────────────────────────────────
const SAMPLE_CONTENT: Record<string, { heading: string; body: string }> = {
  'Executive Summary': {
    heading: 'Executive Summary',
    body: 'This business plan outlines a compelling opportunity in a rapidly growing market. Our company delivers a differentiated solution that addresses a critical pain point for our target customers. With a proven team, clear go-to-market strategy, and strong unit economics, we are positioned to capture significant market share within 24 months of launch.',
  },
  'Market Analysis': {
    heading: 'Market Analysis',
    body: 'The total addressable market (TAM) is estimated at $4.2 billion, growing at 18% CAGR. Our serviceable addressable market (SAM) represents $620 million, with an initial target of $45 million in the first three years. Key market drivers include digital transformation, shifting consumer preferences, and regulatory tailwinds.',
  },
  'Financial Projections': {
    heading: 'Financial Projections',
    body: 'Year 1 revenue target: $480,000 | Year 2: $1.4M | Year 3: $3.8M. Gross margin stabilizes at 68% by Year 2. Break-even projected at Month 14. Operating expenses are front-loaded in Year 1 to support product development and initial customer acquisition.',
  },
  'Business Model': {
    heading: 'Business Model',
    body: 'We operate a subscription-based revenue model with three tiers: Starter ($49/mo), Professional ($149/mo), and Enterprise (custom). Additional revenue streams include professional services, API access, and marketplace commissions. Average contract value is $1,800/year with a target NRR of 115%.',
  },
  'Go-to-Market': {
    heading: 'Go-to-Market Strategy',
    body: 'Phase 1 (Months 1–6): Product-led growth via freemium tier, targeting SMBs through content marketing and SEO. Phase 2 (Months 7–12): Outbound sales motion targeting mid-market accounts. Phase 3 (Year 2): Channel partnerships and enterprise sales team expansion.',
  },
  'Team': {
    heading: 'Team & Leadership',
    body: 'Our founding team brings 40+ years of combined experience in the industry. CEO: 15 years in product leadership at Fortune 500 companies. CTO: Former engineering director with expertise in scalable systems. CMO: Track record of building brands from $0 to $50M ARR. Advisory board includes industry veterans and successful entrepreneurs.',
  },
};

// ─── Page Preview Components ──────────────────────────────────────────────────
interface PagePreview {
  id: number;
  title: string;
  sectionKey: string;
  type: 'cover' | 'content' | 'financial' | 'chart';
}

function generatePagePreviews(template: BusinessPlanTemplate): PagePreview[] {
  const pages: PagePreview[] = [
    { id: 1, title: 'Cover Page', sectionKey: 'cover', type: 'cover' },
  ];

  const contentSections = template.sections.slice(0, 3);
  contentSections.forEach((section, i) => {
    pages.push({
      id: i + 2,
      title: section,
      sectionKey: section,
      type: 'content',
    });
  });

  if (template.sections.includes('Financial Projections')) {
    pages.push({
      id: pages.length + 1,
      title: 'Financial Projections',
      sectionKey: 'Financial Projections',
      type: 'financial',
    });
  } else {
    pages.push({
      id: pages.length + 1,
      title: 'Financial Overview',
      sectionKey: 'Financial Projections',
      type: 'financial',
    });
  }

  if (template.sections.includes('Market Analysis')) {
    pages.push({
      id: pages.length + 1,
      title: 'Market Analysis',
      sectionKey: 'Market Analysis',
      type: 'chart',
    });
  }

  return pages;
}

function CoverPagePreview({ template }: { template: BusinessPlanTemplate }) {
  const [primary, accent] = template.colorPalette;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm h-full flex flex-col">
      <div className="h-3" style={{ background: `linear-gradient(90deg, ${primary}, ${accent})` }} />
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" style={{ background: `linear-gradient(135deg, ${primary}08, ${accent}08)` }}>
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}>
          <i className="ri-file-text-line text-4xl text-white"></i>
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900 mb-2">{template.name}</h1>
        <p className="text-sm text-slate-500 mb-6">Business Plan</p>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1"><i className="ri-pages-line"></i>{template.pageCount} pages</span>
          <span>•</span>
          <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>2024</span>
        </div>
      </div>
      <div className="p-4 border-t border-slate-100 flex items-center justify-between">
        <span className="text-xs text-slate-400">{template.category}</span>
        <span className="text-xs text-slate-300">Page 1 of {template.pageCount}</span>
      </div>
    </div>
  );
}

function ContentPagePreview({ template, page }: { template: BusinessPlanTemplate; page: PagePreview }) {
  const [primary, accent] = template.colorPalette;
  const content = SAMPLE_CONTENT[page.sectionKey] || {
    heading: page.title,
    body: 'This section provides a comprehensive overview of the key aspects of your business, including strategic objectives, operational details, and performance metrics that are critical to your success.',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm h-full flex flex-col">
      <div className="h-2" style={{ background: `linear-gradient(90deg, ${primary}, ${accent})` }} />
      <div className="flex-1 p-6 overflow-hidden">
        <div className="flex items-start justify-between mb-4 pb-3 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: primary }}>{template.category}</div>
            <h3 className="text-base font-extrabold text-slate-900">{template.name}</h3>
          </div>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${primary}18` }}>
            <i className="ri-file-text-line text-sm" style={{ color: primary }}></i>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-4 rounded-full" style={{ background: primary }}></div>
          <h4 className="text-sm font-bold text-slate-800">{content.heading}</h4>
        </div>
        <p className="text-xs text-slate-600 leading-relaxed line-clamp-6">{content.body}</p>
      </div>
      <div className="p-4 border-t border-slate-100 flex items-center justify-between">
        <div className="flex gap-1">
          {[1, 2, 3].map(n => (
            <div key={n} className="w-1.5 h-1.5 rounded-full" style={{ background: n === 1 ? primary : `${primary}30` }} />
          ))}
        </div>
        <span className="text-xs text-slate-300">Page {page.id} of {template.pageCount}</span>
      </div>
    </div>
  );
}

function FinancialPagePreview({ template, page }: { template: BusinessPlanTemplate; page: PagePreview }) {
  const [primary, accent] = template.colorPalette;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm h-full flex flex-col">
      <div className="h-2" style={{ background: `linear-gradient(90deg, ${primary}, ${accent})` }} />
      <div className="flex-1 p-6 overflow-hidden">
        <div className="flex items-start justify-between mb-4 pb-3 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: primary }}>{template.category}</div>
            <h3 className="text-base font-extrabold text-slate-900">{template.name}</h3>
          </div>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${primary}18` }}>
            <i className="ri-bar-chart-2-line text-sm" style={{ color: primary }}></i>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-4 rounded-full" style={{ background: primary }}></div>
          <h4 className="text-sm font-bold text-slate-800">Financial Projections</h4>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 mb-3">
          <p className="text-xs font-semibold text-slate-500 mb-3">Revenue Projection (3-Year)</p>
          <div className="flex items-end gap-2 h-20">
            {[30, 55, 100].map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t-md transition-all" style={{ height: `${h}%`, background: i === 2 ? primary : `${primary}60` }} />
                <span className="text-xs text-slate-400">Y{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Revenue', value: '$3.8M' },
            { label: 'Margin', value: '68%' },
            { label: 'Break-even', value: 'M14' },
          ].map(item => (
            <div key={item.label} className="bg-slate-50 rounded-lg p-2 text-center">
              <p className="text-xs text-slate-400 mb-0.5">{item.label}</p>
              <p className="text-sm font-bold" style={{ color: primary }}>{item.value}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="p-4 border-t border-slate-100 flex items-center justify-between">
        <div className="flex gap-1">
          {[1, 2, 3].map(n => (
            <div key={n} className="w-1.5 h-1.5 rounded-full" style={{ background: n === 1 ? primary : `${primary}30` }} />
          ))}
        </div>
        <span className="text-xs text-slate-300">Page {page.id} of {template.pageCount}</span>
      </div>
    </div>
  );
}

function ChartPagePreview({ template, page }: { template: BusinessPlanTemplate; page: PagePreview }) {
  const [primary, accent] = template.colorPalette;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm h-full flex flex-col">
      <div className="h-2" style={{ background: `linear-gradient(90deg, ${primary}, ${accent})` }} />
      <div className="flex-1 p-6 overflow-hidden">
        <div className="flex items-start justify-between mb-4 pb-3 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: primary }}>{template.category}</div>
            <h3 className="text-base font-extrabold text-slate-900">{template.name}</h3>
          </div>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${primary}18` }}>
            <i className="ri-pie-chart-line text-sm" style={{ color: primary }}></i>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-4 rounded-full" style={{ background: primary }}></div>
          <h4 className="text-sm font-bold text-slate-800">Market Analysis</h4>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 mb-3">
          <p className="text-xs font-semibold text-slate-500 mb-3">Market Size & Growth</p>
          <div className="space-y-2">
            {[
              { label: 'TAM', value: 85, amount: '$4.2B' },
              { label: 'SAM', value: 55, amount: '$620M' },
              { label: 'SOM', value: 25, amount: '$45M' },
            ].map((item, i) => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-600 font-medium">{item.label}</span>
                  <span className="text-xs font-bold" style={{ color: primary }}>{item.amount}</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${item.value}%`, background: i === 0 ? primary : `${primary}${80 - i * 20}` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
          Growing at 18% CAGR with strong tailwinds from digital transformation and regulatory support.
        </p>
      </div>
      <div className="p-4 border-t border-slate-100 flex items-center justify-between">
        <div className="flex gap-1">
          {[1, 2, 3].map(n => (
            <div key={n} className="w-1.5 h-1.5 rounded-full" style={{ background: n === 1 ? primary : `${primary}30` }} />
          ))}
        </div>
        <span className="text-xs text-slate-300">Page {page.id} of {template.pageCount}</span>
      </div>
    </div>
  );
}

function PagePreviewRenderer({ template, page }: { template: BusinessPlanTemplate; page: PagePreview }) {
  switch (page.type) {
    case 'cover':
      return <CoverPagePreview template={template} />;
    case 'financial':
      return <FinancialPagePreview template={template} page={page} />;
    case 'chart':
      return <ChartPagePreview template={template} page={page} />;
    default:
      return <ContentPagePreview template={template} page={page} />;
  }
}

// ─── Thumbnail Carousel ───────────────────────────────────────────────────────
function ThumbnailCarousel({
  template,
  pages,
  activePage,
  onPageChange,
}: {
  template: BusinessPlanTemplate;
  pages: PagePreview[];
  activePage: number;
  onPageChange: (pageId: number) => void;
}) {
  const [primary] = template.colorPalette;

  const handlePrev = () => {
    const currentIndex = pages.findIndex(p => p.id === activePage);
    if (currentIndex > 0) {
      onPageChange(pages[currentIndex - 1].id);
    }
  };

  const handleNext = () => {
    const currentIndex = pages.findIndex(p => p.id === activePage);
    if (currentIndex < pages.length - 1) {
      onPageChange(pages[currentIndex + 1].id);
    }
  };

  const currentIndex = pages.findIndex(p => p.id === activePage);

  return (
    <div className="bg-slate-50 rounded-2xl p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${primary}18` }}>
            <i className="ri-pages-line text-sm" style={{ color: primary }}></i>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-700">Page Preview</p>
            <p className="text-xs text-slate-400">Page {activePage} of {pages.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all cursor-pointer ${
              currentIndex === 0
                ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                : 'border-slate-300 text-slate-600 hover:bg-white hover:border-violet-300 hover:text-violet-600'
            }`}
          >
            <i className="ri-arrow-left-s-line text-base"></i>
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex === pages.length - 1}
            className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all cursor-pointer ${
              currentIndex === pages.length - 1
                ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                : 'border-slate-300 text-slate-600 hover:bg-white hover:border-violet-300 hover:text-violet-600'
            }`}
          >
            <i className="ri-arrow-right-s-line text-base"></i>
          </button>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
        {pages.map((page) => {
          const isActive = page.id === activePage;
          return (
            <button
              key={page.id}
              onClick={() => onPageChange(page.id)}
              className={`flex-shrink-0 w-32 rounded-xl overflow-hidden border-2 transition-all cursor-pointer group ${
                isActive
                  ? 'border-violet-500 shadow-lg scale-105'
                  : 'border-slate-200 hover:border-violet-300 hover:shadow-md'
              }`}
            >
              <div className="bg-white aspect-[3/4] flex items-center justify-center p-2">
                <div className="w-full h-full rounded-lg flex flex-col items-center justify-center" style={{ background: `${primary}08` }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: `${primary}18` }}>
                    <i className={`text-sm ${page.type === 'cover' ? 'ri-file-text-line' : page.type === 'financial' ? 'ri-bar-chart-2-line' : page.type === 'chart' ? 'ri-pie-chart-line' : 'ri-file-list-line'}`} style={{ color: primary }}></i>
                  </div>
                  <div className="w-full space-y-1 px-2">
                    <div className="h-1 bg-slate-200 rounded-full w-full"></div>
                    <div className="h-1 bg-slate-200 rounded-full w-3/4"></div>
                    <div className="h-1 bg-slate-200 rounded-full w-5/6"></div>
                  </div>
                </div>
              </div>
              <div className={`px-2 py-1.5 text-center transition-colors ${isActive ? 'bg-violet-500 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-violet-50'}`}>
                <p className="text-xs font-semibold truncate">{page.title}</p>
                <p className={`text-xs ${isActive ? 'text-violet-100' : 'text-slate-400'}`}>Page {page.id}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SamplePage({ template }: { template: BusinessPlanTemplate }) {
  const [primary, accent] = template.colorPalette;
  const sectionKey = template.sections.find(s => SAMPLE_CONTENT[s]) || template.sections[0];
  const content = SAMPLE_CONTENT[sectionKey] || {
    heading: template.sections[0],
    body: 'This section provides a comprehensive overview of the key aspects of your business, including strategic objectives, operational details, and performance metrics that are critical to your success.',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="h-2" style={{ background: `linear-gradient(90deg, ${primary}, ${accent})` }} />
      <div className="p-6">
        <div className="flex items-start justify-between mb-6 pb-4 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: primary }}>{template.category}</div>
            <h3 className="text-lg font-extrabold text-slate-900">{template.name}</h3>
            <p className="text-xs text-slate-400 mt-0.5">Business Plan · {template.pageCount} pages</p>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${primary}18` }}>
            <i className="ri-file-text-line text-lg" style={{ color: primary }}></i>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-5 rounded-full" style={{ background: primary }}></div>
          <h4 className="text-sm font-bold text-slate-800">{content.heading}</h4>
        </div>
        <p className="text-xs text-slate-600 leading-relaxed mb-5">{content.body}</p>
        <div className="bg-slate-50 rounded-lg p-4">
          <p className="text-xs font-semibold text-slate-500 mb-3">Revenue Projection (3-Year)</p>
          <div className="flex items-end gap-2 h-16">
            {[30, 55, 100].map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t-md transition-all" style={{ height: `${h}%`, background: i === 2 ? primary : `${primary}60` }} />
                <span className="text-xs text-slate-400">Y{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-1">
            {[1, 2, 3].map(n => (
              <div key={n} className="w-1.5 h-1.5 rounded-full" style={{ background: n === 1 ? primary : `${primary}30` }} />
            ))}
          </div>
          <span className="text-xs text-slate-300">Page 1 of {template.pageCount}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function TemplatePreviewModal({ 
  template: initialTemplate, 
  onClose, 
  onUse, 
  onSwitchTemplate,
  isFavorite,
  onToggleFavorite,
}: Props) {
  const [activeTemplate, setActiveTemplate] = useState<BusinessPlanTemplate>(initialTemplate);
  const [activeTab, setActiveTab] = useState<'overview' | 'sections' | 'sample' | 'similar'>('sample');
  const [primary, accent] = activeTemplate.colorPalette;
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  // Page carousel state
  const pagePreviews = useMemo(() => generatePagePreviews(activeTemplate), [activeTemplate]);
  const [activePageId, setActivePageId] = useState(1);

  const handleSwitch = (t: BusinessPlanTemplate) => {
    setActiveTemplate(t);
    setActiveTab('sample');
    setActivePageId(1);
    onSwitchTemplate?.(t);
  };

  const similarCount = useMemo(
    () => scoreTemplates(activeTemplate, businessPlanTemplates).length,
    [activeTemplate]
  );

  const handleShare = () => {
    const url = new URL(window.location.href);
    url.pathname = '/business-plan-writer';
    url.searchParams.set('template', activeTemplate.id);
    navigator.clipboard.writeText(url.toString()).then(() => {
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2500);
    });
  };

  const activePage = pagePreviews.find(p => p.id === activePageId) || pagePreviews[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Copied toast */}
      <div
        className={`fixed top-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2.5 px-5 py-3 bg-slate-900 text-white text-sm font-semibold rounded-2xl shadow-2xl border border-slate-700 transition-all duration-300 ${
          copyState === 'copied' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'
        }`}
      >
        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-500">
          <i className="ri-check-line text-xs text-white"></i>
        </div>
        Link copied to clipboard!
      </div>

      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-start gap-5 p-7 border-b border-slate-100 flex-shrink-0">
          <div
            className="w-16 h-16 rounded-2xl flex-shrink-0 flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${primary}22, ${accent}33)` }}
          >
            <i className="ri-file-text-line text-2xl" style={{ color: primary }}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{activeTemplate.category}</span>
              {activeTemplate.badge && (
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${BADGE_STYLES[activeTemplate.badge]}`}>{activeTemplate.badge}</span>
              )}
            </div>
            <h2 className="text-xl font-extrabold text-slate-900 leading-tight mb-1">{activeTemplate.name}</h2>
            <p className="text-sm text-slate-500 line-clamp-2">{activeTemplate.description}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1"><i className="ri-pages-line"></i>{activeTemplate.pageCount} pages</span>
              <span className="flex items-center gap-1"><i className="ri-list-check-2"></i>{activeTemplate.sections.length} sections</span>
              <span className="flex items-center gap-1"><i className="ri-ai-generate"></i>AI-powered</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Heart/Favorite button */}
            <button
              onClick={() => onToggleFavorite(activeTemplate.id)}
              className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all cursor-pointer ${
                isFavorite
                  ? 'border-rose-300 bg-rose-50 text-rose-600'
                  : 'border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-rose-500'
              }`}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <i className={`text-lg ${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
            </button>
            {/* Share button in header */}
            <button
              onClick={handleShare}
              className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-all cursor-pointer ${
                copyState === 'copied'
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                  : 'border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-700'
              }`}
              title="Copy shareable link"
            >
              <i className={`text-lg ${copyState === 'copied' ? 'ri-check-line' : 'ri-share-line'}`}></i>
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition-colors cursor-pointer flex-shrink-0">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-7 pt-4 flex-shrink-0 overflow-x-auto">
          {(['overview', 'sections', 'sample', 'similar'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap capitalize flex items-center gap-1.5 ${activeTab === tab ? 'bg-violet-50 text-violet-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
            >
              {tab === 'overview' && <><i className="ri-eye-line"></i>Overview</>}
              {tab === 'sections' && <><i className="ri-list-check-2"></i>All Sections</>}
              {tab === 'sample' && <><i className="ri-file-paper-line"></i>Sample Pages</>}
              {tab === 'similar' && (
                <>
                  <i className="ri-sparkling-2-line"></i>
                  Similar
                  {similarCount > 0 && (
                    <span className="ml-0.5 text-xs bg-violet-600 text-white rounded-full w-4 h-4 flex items-center justify-center font-bold leading-none">
                      {similarCount}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-7 py-5">

          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Style Tags</p>
                <div className="flex flex-wrap gap-2">
                  {activeTemplate.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Color Palette</p>
                <div className="flex items-center gap-3">
                  {activeTemplate.colorPalette.map((color, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg border border-slate-200 shadow-sm" style={{ background: color }} />
                      <span className="text-xs text-slate-500 font-mono">{color}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">What's Included</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: 'ri-file-text-line', label: `${activeTemplate.pageCount} pages of content` },
                    { icon: 'ri-list-check-2', label: `${activeTemplate.sections.length} structured sections` },
                    { icon: 'ri-bar-chart-2-line', label: 'Financial model & charts' },
                    { icon: 'ri-ai-generate', label: 'AI content generation' },
                    { icon: 'ri-download-line', label: 'PDF & Word export' },
                    { icon: 'ri-edit-line', label: 'Fully customizable' },
                    { icon: 'ri-shield-check-line', label: 'Investor-ready format' },
                    { icon: 'ri-translate-2', label: 'Multi-language support' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-2.5 p-3 bg-slate-50 rounded-xl">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: `${primary}18` }}>
                        <i className={`${item.icon} text-sm`} style={{ color: primary }}></i>
                      </div>
                      <span className="text-xs text-slate-700 font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100 rounded-2xl p-5">
                <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-2">Best For</p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  This template is ideal for <strong>{activeTemplate.category}</strong> businesses looking to create a professional, investor-ready business plan. Perfect for entrepreneurs seeking funding, strategic planning, or launching a new venture.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'sections' && (
            <div className="space-y-2">
              <p className="text-xs text-slate-400 mb-4">All {activeTemplate.sections.length} sections included in this template</p>
              {activeTemplate.sections.map((section, i) => (
                <div key={section} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-violet-200 hover:bg-violet-50/30 transition-all group">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{ background: `${primary}18`, color: primary }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-800 group-hover:text-violet-700 transition-colors">{section}</p>
                  </div>
                  <i className="ri-check-line text-emerald-500 text-sm flex-shrink-0"></i>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'sample' && (
            <div className="space-y-4">
              {/* Thumbnail Carousel */}
              <ThumbnailCarousel
                template={activeTemplate}
                pages={pagePreviews}
                activePage={activePageId}
                onPageChange={setActivePageId}
              />

              {/* Full Page Preview */}
              <div className="transition-all duration-300">
                <PagePreviewRenderer template={activeTemplate} page={activePage} />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <i className="ri-information-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-xs text-amber-700 leading-relaxed">
                  These are sample previews. The full plan will be generated with your actual business details, custom financial data, and AI-written content tailored to your industry.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'similar' && (
            <SimilarTemplatesPanel
              source={activeTemplate}
              onSwitch={handleSwitch}
              onUse={onUse}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-5 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
            Back to Templates
          </button>
          <div className="flex items-center gap-3">
            {/* Heart button in footer */}
            <button
              onClick={() => onToggleFavorite(activeTemplate.id)}
              className={`px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                isFavorite
                  ? 'border-rose-300 bg-rose-50 text-rose-700'
                  : 'border-slate-200 text-slate-600 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600'
              }`}
            >
              <i className={`text-base ${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
              {isFavorite ? 'Saved' : 'Save'}
            </button>
            {/* Share button in footer */}
            <button
              onClick={handleShare}
              className={`px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                copyState === 'copied'
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <i className={`text-base ${copyState === 'copied' ? 'ri-check-line' : 'ri-share-line'}`}></i>
              {copyState === 'copied' ? 'Link Copied!' : 'Share'}
            </button>
            <button
              onClick={() => setActiveTab('similar')}
              className="px-4 py-2.5 rounded-xl border border-violet-200 text-sm font-semibold text-violet-600 hover:bg-violet-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-sparkling-2-line"></i>
              See Similar
            </button>
            <button
              onClick={onUse}
              className="px-7 py-2.5 rounded-xl text-white text-sm font-bold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center gap-2 shadow-lg"
              style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}
            >
              <i className="ri-edit-line"></i>
              Use This Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
